import React from 'react';
import _get from 'lodash/get';
import _isObject from 'lodash/isObject';
import _isFunction from 'lodash/isFunction';

import { useFeatures } from 'utils/hooks';
import { UiKit, PageErrorUi, SectionContainer } from 'components';
import {
  Features,
  LoaderSkeleton
} from 'views/Configure/ReturnManagement/AdditionalFeatures/components';
import { MODAL_GENERATOR } from './stubs';

export default function AdditionalFeatureSection({
  includeFeature = [],
  excludeFeature = [],
  title = '',
  loaderCount = 3,
  hideErrorImage = false,
  customModalToggle = null,
  className = '',
  containerClassName = '',
  externalLoading = false,
  isConfirmationRequired = true
}) {
  const {
    features,
    featureMap,
    isLoading,
    isError,
    onReload,
    isModalOpen,
    modalData,
    isProcessing,
    processingId,
    onToggle,
    onModalClose,
    onModalClosed,
    toggleFeature
  } = useFeatures({
    includeFeatures: includeFeature,
    excludeFeatures: excludeFeature,
    isConfirmationRequired
  });

  const modalTitle = _get(modalData, 'title', '');
  const featureSlug = _get(modalData, 'feature_type', '');
  const isCurrentlyEnabled = _get(modalData, 'is_enabled', false);
  const modalGenerator = _get(MODAL_GENERATOR, featureSlug, () => ({}));
  const newState = isCurrentlyEnabled ? 'disable' : 'enable';

  const {
    confirmationMessage = `You are about to ${newState} the feature. Are you sure?`,
    confirmButtonText = 'Confirm'
  } = modalGenerator(!isCurrentlyEnabled);

  const onToggleFeature = featureId => {
    if (customModalToggle && _isObject(customModalToggle)) {
      const feature = _get(featureMap, featureId, null);
      if (!feature) return;

      const isEnabled = _get(feature, 'is_enabled', false);
      const featureType = _get(feature, 'feature_type', '');

      const handlerKey = isEnabled ? 'onDisable' : 'onEnable';
      const handler = _get(customModalToggle, `${featureType}.${handlerKey}`);
      if (_isFunction(handler)) {
        handler(featureId, onReload);
        return;
      }
    }

    onToggle(featureId);
  };

  return (
    <SectionContainer
      title={title}
      className={className}
      containerClassName={containerClassName}
    >
      <LoaderSkeleton
        isLoading={isLoading || externalLoading}
        count={loaderCount}
      >
        <PageErrorUi
          isError={isError}
          onReload={onReload}
          hideImage={hideErrorImage}
        >
          <Features
            list={features}
            processingId={processingId}
            onToggle={onToggleFeature}
          />
          <UiKit.Modal
            isOpen={isModalOpen}
            isProcessing={isProcessing}
            onClose={onModalClose}
            onClosed={onModalClosed}
          >
            <UiKit.Modal.Header title={modalTitle} />
            <UiKit.Modal.Body>
              <p>{confirmationMessage}</p>
            </UiKit.Modal.Body>
            <UiKit.Modal.Footer
              onConfirm={toggleFeature}
              confirmBtnText={confirmButtonText}
              confirmBtnVariant={isCurrentlyEnabled ? 'destructive' : 'primary'}
            />
          </UiKit.Modal>
        </PageErrorUi>
      </LoaderSkeleton>
    </SectionContainer>
  );
}
